"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useCallback, type ReactNode } from "react";
import { useFeatureAnnouncement } from "../model";
import { useMyFeedbackUnreadCount } from "../model/use-feedback-unread-count";
import { useScrollDirection } from "../model/use-scroll-direction";
import { useDraggablePosition } from "../model/use-draggable-position";
import { useAnnouncementStore } from "../model/use-announcement-store";
import { FeatureAnnouncementFabGroup } from "./feature-announcement-fab-group";
import { TutorialModal } from "./tutorial-modal";
import { FeedbackSidebar } from "./feedback-sidebar";
import { FeedbackModal } from "./feedback-modal";

/**
 * `yourFeedbackContent` is a slot for the user's "Your feedback" tab. It is
 * supplied by `app/layout.tsx` (which holds the `@/features/*` import), NOT by
 * this entity — so the entity never imports the feature and the dependency
 * direction stays legal. When omitted, the widget shows only the give-feedback form.
 */
export function GlobalFeatureAnnouncement(t0) {
  const $ = _c(53);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    yourFeedbackContent
  } = t1;
  const {
    featureKey,
    announcement,
    announcementType,
    isAddonFeature,
    addonAccent,
    feedbackEnabled,
    feedbackDisplayMode,
    displayPosition,
    isAnnouncementVisible,
    isModalOpen,
    isSidebarOpen,
    openModal,
    closeModal,
    openSidebar,
    closeSidebar,
    dismissGlobal
  } = useFeatureAnnouncement();
  const {
    isScrollingDown
  } = useScrollDirection();
  const {
    corner,
    style: positionStyle,
    onDragEnd,
    isDragEnabled
  } = useDraggablePosition();
  const setTopBarConfig = useAnnouncementStore(_temp);
  let t2;
  if ($[2] !== feedbackEnabled) {
    t2 = {
      enabled: feedbackEnabled
    };
    $[2] = feedbackEnabled;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const feedbackUnreadCount = useMyFeedbackUnreadCount(t2);
  const isFeedbackOpen = isSidebarOpen;
  let t3;
  if ($[4] !== announcementType || $[5] !== closeModal || $[6] !== dismissGlobal) {
    t3 = () => {
      if (announcementType === "global") {
        dismissGlobal();
      }
      closeModal();
    };
    $[4] = announcementType;
    $[5] = closeModal;
    $[6] = dismissGlobal;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleModalClose = t3;
  let t4;
  if ($[8] !== closeSidebar || $[9] !== isFeedbackOpen || $[10] !== openSidebar) {
    t4 = () => {
      if (isFeedbackOpen) {
        closeSidebar();
      } else {
        openSidebar();
      }
    };
    $[8] = closeSidebar;
    $[9] = isFeedbackOpen;
    $[10] = openSidebar;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleFeedbackClick = t4;
  let t5;
  let t6;
  if ($[12] !== displayPosition || $[13] !== feedbackEnabled || $[14] !== handleFeedbackClick || $[15] !== isAnnouncementVisible || $[16] !== openModal || $[17] !== setTopBarConfig) {
    t5 = () => {
      if (displayPosition === "top-bar" || displayPosition === "both") {
        setTopBarConfig({
          isAnnouncementVisible,
          feedbackEnabled,
          onSparkleClick: openModal,
          onFeedbackClick: handleFeedbackClick
        });
      } else {
        setTopBarConfig(null);
      }
      return () => {
        setTopBarConfig(null);
      };
    };
    t6 = [displayPosition, isAnnouncementVisible, feedbackEnabled, handleFeedbackClick, openModal, setTopBarConfig];
    $[12] = displayPosition;
    $[13] = feedbackEnabled;
    $[14] = handleFeedbackClick;
    $[15] = isAnnouncementVisible;
    $[16] = openModal;
    $[17] = setTopBarConfig;
    $[18] = t5;
    $[19] = t6;
  } else {
    t5 = $[18];
    t6 = $[19];
  }
  useEffect(t5, t6);
  let t7;
  if ($[20] !== corner || $[21] !== displayPosition || $[22] !== feedbackDisplayMode || $[23] !== feedbackEnabled || $[24] !== feedbackUnreadCount || $[25] !== handleFeedbackClick || $[26] !== isAddonFeature || $[27] !== isAnnouncementVisible || $[28] !== isDragEnabled || $[29] !== isFeedbackOpen || $[30] !== isModalOpen || $[31] !== isScrollingDown || $[32] !== onDragEnd || $[33] !== openModal || $[34] !== positionStyle) {
    t7 = (displayPosition === "floating" || displayPosition === "both") && <FeatureAnnouncementFabGroup isAnnouncementVisible={isAnnouncementVisible} feedbackEnabled={feedbackEnabled} feedbackDisplayMode={feedbackDisplayMode} isSidebarOpen={isFeedbackOpen} isModalOpen={isModalOpen} onSparkleClick={openModal} onFeedbackClick={handleFeedbackClick} feedbackUnreadCount={feedbackUnreadCount} isHidden={isScrollingDown} corner={corner} positionStyle={positionStyle} onDragEnd={onDragEnd} isDragEnabled={isDragEnabled} isAddonFeature={isAddonFeature} />;
    $[20] = corner;
    $[21] = displayPosition;
    $[22] = feedbackDisplayMode;
    $[23] = feedbackEnabled;
    $[24] = feedbackUnreadCount;
    $[25] = handleFeedbackClick;
    $[26] = isAddonFeature;
    $[27] = isAnnouncementVisible;
    $[28] = isDragEnabled;
    $[29] = isFeedbackOpen;
    $[30] = isModalOpen;
    $[31] = isScrollingDown;
    $[32] = onDragEnd;
    $[33] = openModal;
    $[34] = positionStyle;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  let t8;
  if ($[36] !== addonAccent || $[37] !== announcement || $[38] !== featureKey || $[39] !== handleModalClose || $[40] !== isModalOpen) {
    t8 = announcement && <TutorialModal announcement={announcement} isOpen={isModalOpen} onClose={handleModalClose} featureKey={featureKey} addonAccent={addonAccent} />;
    $[36] = addonAccent;
    $[37] = announcement;
    $[38] = featureKey;
    $[39] = handleModalClose;
    $[40] = isModalOpen;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== closeSidebar || $[43] !== featureKey || $[44] !== feedbackDisplayMode || $[45] !== feedbackUnreadCount || $[46] !== isFeedbackOpen || $[47] !== yourFeedbackContent) {
    t9 = feedbackDisplayMode === "sidebar" ? <FeedbackSidebar isOpen={isFeedbackOpen} onClose={closeSidebar} featureKey={featureKey} yourFeedbackContent={yourFeedbackContent} feedbackUnreadCount={feedbackUnreadCount} /> : <FeedbackModal isOpen={isFeedbackOpen} onClose={closeSidebar} featureKey={featureKey} yourFeedbackContent={yourFeedbackContent} feedbackUnreadCount={feedbackUnreadCount} />;
    $[42] = closeSidebar;
    $[43] = featureKey;
    $[44] = feedbackDisplayMode;
    $[45] = feedbackUnreadCount;
    $[46] = isFeedbackOpen;
    $[47] = yourFeedbackContent;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  let t10;
  if ($[49] !== t7 || $[50] !== t8 || $[51] !== t9) {
    t10 = <>{t7}{t8}{t9}</>;
    $[49] = t7;
    $[50] = t8;
    $[51] = t9;
    $[52] = t10;
  } else {
    t10 = $[52];
  }
  return t10;
}
function _temp(s) {
  return s.setTopBarConfig;
}